import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./report.css";

const Icon = ({ children, size = 16, className }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);
const SpinnerIcon = ({ size }) => (
  <Icon size={size} className="wr-spinner"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></Icon>
);
const FileIcon = () => (
  <Icon size={20}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
  </Icon>
);
const EyeIcon = () => (
  <Icon size={14}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></Icon>
);
const DownloadIcon = () => (
  <Icon size={14}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></Icon>
);

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
const fmtTime = (d) =>
  new Date(d).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

const WalletReports = () => {
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
  const userId = localStorage.getItem("userId");
  const token = localStorage.getItem("token");

  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null); // { id, mode }
  const [actionError, setActionError] = useState("");

  const fetchReports = useCallback(async (signal) => {
    if (!userId) {
      setError("User not authenticated");
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API_URL}/reportsWithdraw/user/${userId}`, {
        signal,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true,
      });
      const data = res.data?.reports || res.data || [];
      setReports(Array.isArray(data) ? data : []);
    } catch (err) {
      if (axios.isCancel(err)) return;
      console.error("Error loading reports:", err);
      setError("Failed to load withdrawal reports");
    } finally {
      if (!signal?.aborted) setIsLoading(false);
    }
  }, [API_URL, userId, token]);

  useEffect(() => {
    const c = new AbortController();
    fetchReports(c.signal);
    return () => c.abort();
  }, [fetchReports]);

  const sorted = useMemo(
    () => [...reports].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [reports]
  );

  /* "view" ouvre un onglet ; "download" télécharge le fichier.
     L'onglet est ouvert AVANT l'appel réseau : sinon les navigateurs
     (surtout mobile/Safari) bloquent la popup. */
  const handleReport = async (report, mode) => {
    if (!report?._id || busy) return;
    setBusy({ id: report._id, mode });
    setActionError("");
    const win = mode === "view" ? window.open("", "_blank") : null;
    if (win) win.opener = null;
    try {
      const res = await axios.get(`${API_URL}/reportsWithdraw/view/${report._id}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
        timeout: 30000,
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (win) {
        win.location.href = url;
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = `withdrawal-report-${fmtDate(report.createdAt).replace(/\s/g, "-")}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000); // 1s coupait le chargement du PDF
    } catch (err) {
      console.error("Error opening PDF:", err);
      if (win) win.close();
      setActionError("Unable to open the report. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  if (isLoading)
    return (
      <div className="wr-root" role="status" aria-live="polite">
        <div className="wr-skeleton" aria-hidden="true">
          {[0, 1, 2].map((i) => <div key={i} className="wr-skel-row" />)}
        </div>
        <span className="wr-sr">Loading reports…</span>
      </div>
    );

  if (error)
    return (
      <div className="wr-root">
        <div className="wr-state" role="alert">
          <p className="wr-state__title">Something went wrong</p>
          <p className="wr-state__sub">{error}</p>
          {userId && <button type="button" className="wr-retry" onClick={() => fetchReports()}>Try again</button>}
        </div>
      </div>
    );

  if (sorted.length === 0)
    return (
      <div className="wr-root">
        <div className="wr-state">
          <p className="wr-state__title">No reports yet</p>
          <p className="wr-state__sub">Your withdrawal reports will appear here.</p>
        </div>
      </div>
    );

  return (
    <div className="wr-root">
      <div className="wr-header">
        <h2 className="wr-title">Withdrawal reports</h2>
        <span className="wr-count">{sorted.length} report{sorted.length > 1 ? "s" : ""}</span>
      </div>

      {actionError && (
        <div className="wr-toast" role="alert">
          <span>{actionError}</span>
          <button type="button" onClick={() => setActionError("")} aria-label="Dismiss">✕</button>
        </div>
      )}

      <ul className="wr-list">
        {sorted.map((r) => {
          const viewing = busy?.id === r._id && busy.mode === "view";
          const downloading = busy?.id === r._id && busy.mode === "download";
          return (
            <li key={r._id} className="wr-item">
              <span className="wr-icon-wrap"><FileIcon /></span>
              <div className="wr-meta">
                <span className="wr-date">{fmtDate(r.createdAt)}</span>
                <span className="wr-sub">Withdrawal report · {fmtTime(r.createdAt)}</span>
              </div>
              <div className="wr-actions">
                <button type="button" className="wr-btn wr-btn--view" disabled={!!busy}
                  onClick={() => handleReport(r, "view")}
                  aria-label={`View report from ${fmtDate(r.createdAt)}`}>
                  {viewing ? <SpinnerIcon size={14} /> : <EyeIcon />}
                  <span className="wr-btn__label">{viewing ? "Opening…" : "View"}</span>
                </button>
                <button type="button" className="wr-btn wr-btn--ghost" disabled={!!busy}
                  onClick={() => handleReport(r, "download")}
                  aria-label={`Download report from ${fmtDate(r.createdAt)}`}>
                  {downloading ? <SpinnerIcon size={14} /> : <DownloadIcon />}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default WalletReports;